import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Block, BlockTitle, Button, EmptyState, Meta,
  SegmentedControl, SearchBar, ConfirmDialog, Spinner, Banner,
  // The kit's schema marks — one vocabulary across every ofself surface.
  schemaGlyph, hasGlyph,
} from '@ofself/shell';
import {
  scheduleLines, withReturnTo, runInputs, missingRunMessage, continuesInBackground, formatBytes,
} from '../lib/pluginSchedule.mjs';

// THE PAGE WAS TWO PAGES WEARING ONE LIST. A connection is a CREDENTIAL — an
// account you link once, that a provider can revoke without telling you. A
// plugin is a CAPABILITY — something you run, that either works or names what
// is stopping it. They were interleaved in one flat column, so the page led
// with the thing you have to set up rather than the thing you came to do, and
// neither question could be answered by scanning.
//
// Two blocks now, in the order the work happens: connect, then run.

// INTEGRATIONS / PLUGINS — ported from views/integrations/plugins.js
// (2026-08-24). One list: an integration IS a plugin that brings in external
// data, so that is a badge, not a category. Registered under both names —
// 'plugins' is where it lives, 'integrations' because the OAuth callback
// redirects to ?view=integrations and old links exist.
//
// The acting identity comes through window.igIdentity() (app.js's seam) — the
// legacy view reached `App` by name in the shared global scope, which a React
// module cannot. cronInWords stays in the legacy cron.js classic script.

const api = { get: (p) => window.igApi.get(p) };
const toast = (m, t) => window.showToast?.(m, t);
const identity = () => (window.igIdentity ? window.igIdentity() : {});

const INTEGRATIONS = [
  {
    id: 'google', name: 'Google', category: 'productivity',
    // WHAT CONNECTING ACTUALLY GRANTS — the consent screen asks for five
    // products; `services` stays shorter because it lists what the ROUTE sync
    // can run (Drive and YouTube are plugin-only).
    desc: 'Calendar, contacts, email, Drive, and YouTube',
    services: ['calendar', 'contacts', 'gmail'],
    // CONNECTING MOVED TO PARADIGM. Google's refresh token is held by the
    // broker now and lent to the plugin per call, so this app stores no Google
    // credential at all. Sending someone through the old flow would mint a
    // SECOND connection into a vault nothing reads — two grants for one
    // account, one of them invisible and un-revokable from anywhere a person
    // looks.
    //
    // The other providers keep their own flow until their credential moves too;
    // a provider with nothing declared on Paradigm has nothing to connect to
    // there. See `connectVia`.
    connectVia: 'paradigm',
    paradigmPluginId: 'ed527c71-0c90-4dab-93ef-8c174fa68896',
    connectUrl: '/integrations/google/connect',
    statusUrl: '/integrations/google/status',
    syncUrl: '/integrations/google/sync',
    unlinkUrl: '/integrations/google/unlink',
    // Declared ahead of the plugin existing — lights up the schedule and
    // last-run lines the moment "Google Sync" is registered.
    pluginNames: ['Google Sync'],
    icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11z" stroke="currentColor" stroke-width="1.2"/><path d="M9 1v2M9 15v2M1 9h2M15 9h2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" opacity=".5"/></svg>',
  },
  {
    id: 'github', name: 'GitHub', category: 'development',
    desc: 'Repositories, pushes, and pull requests',
    services: ['repos', 'pushes', 'pull-requests'],
    // Same move as Google: the refresh path is Paradigm's now, so connecting
    // here would mint a second credential into a vault nothing reads.
    connectVia: 'paradigm',
    paradigmPluginId: 'e7fc79bf-465e-423e-bce5-9d8945819434',
    connectUrl: '/integrations/github/connect',
    statusUrl: '/integrations/github/status',
    syncUrl: '/integrations/github/sync',
    unlinkUrl: '/integrations/github/unlink',
    pluginNames: ['GitHub Sync'],
    icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9c0 3.31 2.15 6.12 5.13 7.12.38.07.52-.16.52-.36v-1.26c-2.09.45-2.53-1.01-2.53-1.01-.34-.87-.84-1.1-.84-1.1-.68-.47.05-.46.05-.46.76.05 1.16.78 1.16.78.67 1.15 1.76.82 2.19.63.07-.49.26-.82.48-1.01-1.67-.19-3.42-.83-3.42-3.72 0-.82.29-1.49.78-2.02-.08-.19-.34-.96.07-1.99 0 0 .63-.2 2.08.77a7.26 7.26 0 013.8 0c1.44-.97 2.08-.77 2.08-.77.41 1.03.15 1.8.08 1.99.48.53.77 1.2.77 2.02 0 2.9-1.76 3.53-3.44 3.72.27.23.52.7.52 1.41v2.1c0 .2.14.44.52.36A7.5 7.5 0 009 1.5z" stroke="currentColor" stroke-width="1.1"/></svg>',
  },
  {
    id: 'spotify', name: 'Spotify', category: 'media',
    // One service, and it is a window rather than a history — no "from" date,
    // no backfill. No syncUrl: the reading is the plugin's.
    desc: 'Recently played tracks',
    services: ['recently-played'],
    connectUrl: '/integrations/spotify/connect',
    statusUrl: '/integrations/spotify/status',
    unlinkUrl: '/integrations/spotify/unlink',
    pluginNames: ['Spotify Sync'],
    icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7.5" stroke="currentColor" stroke-width="1.1"/><path d="M5.5 7.2c2.3-.7 4.9-.5 7 .7M6 9.6c1.9-.55 4-.35 5.7.6M6.5 11.9c1.5-.4 3.1-.25 4.4.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>',
  },
  {
    id: 'lichess', name: 'Lichess', category: 'gaming',
    desc: 'Chess games and ratings',
    services: ['games'],
    statusUrl: '/integrations/lichess/status',
    linkUrl: '/integrations/lichess/link',
    syncUrl: '/integrations/lichess/sync',
    unlinkUrl: '/integrations/lichess/unlink',
    usernameLink: true,
    icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M6 15h6M7.5 15v-3h3v3M5 12h8M9 2l3 4-2 1 3 5H5l3-5-2-1 3-4z" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/></svg>',
  },
];

// A plugin with no integration behind it still needs a mark in the icon cell.
const PLUGIN_GLYPH = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="5" cy="5" r="2.6" stroke="currentColor" stroke-width="1.1"/><circle cx="13" cy="5" r="2.6" stroke="currentColor" stroke-width="1.1"/><circle cx="9" cy="13" r="2.6" stroke="currentColor" stroke-width="1.1"/><path d="M7.6 5.6h2.8M6.4 7.2l1.4 3.4M11.6 7.2l-1.4 3.4" stroke="currentColor" stroke-width="1" opacity=".5"/></svg>';

// Every integration endpoint has a group-scoped twin under /group/:id/ — the
// backend is the authority on which support it; one that hasn't grown group
// routes simply answers "disconnected".
function urls(integ) {
  const gid = identity().activeGroupId || null;
  if (integ.connectVia === 'paradigm' && integ.paradigmPluginId) {
    // No `connect` here: connecting happens on Paradigm's setup page for the
    // plugin, whose URL (host + group) comes from the backend as the plugin's
    // `setup_url` — SourceRow fills it in. No Paradigm host is baked in here.
    // Status, sync and unlink still answer here: this app is still the
    // execution host and still reports what it ran. Only the CREDENTIAL moved.
    if (!gid) {
      return {
        status: integ.statusUrl, sync: integ.syncUrl,
        unlink: integ.unlinkUrl,
        connection: `/integrations/${integ.id}/connection`,
      };
    }
    const gb = `/integrations/${integ.id}/group/${encodeURIComponent(gid)}`;
    return {
      status: `${gb}/status`, sync: `${gb}/sync`, unlink: `${gb}/unlink`,
      connection: `${gb}/connection`,
    };
  }
  if (!gid) {
    return {
      status: integ.statusUrl, sync: integ.syncUrl,
      unlink: integ.unlinkUrl, connect: integ.connectUrl,
      connection: `/integrations/${integ.id}/connection`,
    };
  }
  const base = `/integrations/${integ.id}/group/${encodeURIComponent(gid)}`;
  return {
    status: `${base}/status`,
    sync: `${base}/sync`,
    unlink: `${base}/unlink`,
    connection: `${base}/connection`,
    connect: integ.connectUrl
      ? `${integ.connectUrl}?group_id=${encodeURIComponent(gid)}`
      : null,
  };
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// How long until a parked run expires — a lapsed consent link looks identical
// to a live one.
function timeUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'now — request it again';
  const mins = Math.round(ms / 60000);
  return mins < 1 ? 'in under a minute' : `in ${mins}m`;
}

// ── Where a sync has got to ────────────────────────────────────────────────
// `last_synced[service]` is a CURSOR — a position, not "when it last ran".
// Both clocks are stated because they come apart exactly when it matters.
function RanAt({ ranAt }) {
  if (!ranAt) return null;
  const d = new Date(ranAt);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  const ago = mins < 1 ? 'just now'
    : mins < 60 ? `${mins}m ago`
    : mins < 1440 ? `${Math.floor(mins / 60)}h ago`
    : `${Math.floor(mins / 1440)}d ago`;
  return <span className="int-cursor-ran" title={`last run ${d.toISOString()}`}>ran {ago}</span>;
}

// What the LAST RUN looked at. A null `from` is not missing data, it IS the
// fact: that run had no floor.
function Coverage({ coverage }) {
  if (!coverage || (!coverage.from && !coverage.to)) return null;
  const fmt = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };
  const a = coverage.from ? fmt(coverage.from) : null;
  const b = coverage.to ? fmt(coverage.to) : null;
  const text = a && b ? `covered ${a} → ${b}`
    : b ? `covered everything up to ${b}`
    : a ? `covered from ${a}` : '';
  if (!text) return null;
  const title = `from ${coverage.from || 'the beginning'} to ${coverage.to || 'wherever it reached'}`;
  return <span className="int-cursor-kind" title={title}>{text}</span>;
}

function CursorLabel({ cursor, ranAt, coverage }) {
  if (!cursor) {
    // A run that happened and moved nothing is NOT never having run.
    const ran = ranAt ? <RanAt ranAt={ranAt} /> : null;
    return (
      <span className="int-cursor int-cursor-none">
        {ran ? 'nothing new yet' : 'never synced'}
        {ran
          ? <span className="int-cursor-kind">{ran}</span>
          : <span className="int-cursor-kind">starts from the recent window</span>}
        <Coverage coverage={coverage} />
      </span>
    );
  }
  const d = new Date(cursor);
  if (Number.isNaN(d.getTime())) return <>{String(cursor)}</>;
  const abs = d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
  // Caught up, or still draining? The distance is stated, not left for the
  // reader to compute against today's date.
  const behindMs = Date.now() - d.getTime();
  const behindDays = Math.floor(behindMs / 86400000);
  return (
    <span className="int-cursor" title={`resumes from ${d.toISOString()}`}>
      caught up to {abs}
      {behindMs < 36 * 3600 * 1000
        ? <span className="int-cursor-kind int-cursor-current">up to date</span>
        : <span className="int-cursor-kind int-cursor-behind">{behindDays}d of backlog left</span>}
      {ranAt ? <span className="int-cursor-kind"><RanAt ranAt={ranAt} /></span> : null}
      <Coverage coverage={coverage} />
    </span>
  );
}

// AT MODULE SCOPE, NOT INSIDE Plugins. A component declared in a render body is
// a new type on every render, so React tears it down and remounts it: the input
// loses focus, typed text is wiped, and the page jumps. The other fragments in
// Plugins hold no state and are called as functions for the same reason.
function UsernameConnect({ integ, onLink }) {
  const [username, setUsername] = useState('');
  return (
    <div className="int-connect-need">
      <div className="int-connect-need-label">
        your {integ.name} username — it is a public profile, so nothing is authorised
      </div>
      <div className="int-username-form">
        <input type="text" className="int-username-input" placeholder={`${integ.name.toLowerCase()} username`}
               value={username} onChange={(e) => setUsername(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLink(integ.id, username.trim()); } }} />
        <Button size="sm" variant="solid" disabled={!username.trim()}
                onClick={() => onLink(integ.id, username.trim())}>
          Connect
        </Button>
      </div>
    </div>
  );
}

export default function Plugins({ active }) {
  const [statuses, setStatuses] = useState({});
  const [plugins, setPlugins] = useState(null); // null = not fetched, [] = empty
  const [pluginsError, setPluginsError] = useState(null);
  const [pluginInputs, setPluginInputs] = useState({});
  // Per `file` field (keyed plugin::param): { uploading, progress, error, over }.
  // The uploaded file itself lives in pluginInputs as { id, filename, size }.
  const [pluginUploads, setPluginUploads] = useState({});
  const [pluginRuns, setPluginRuns] = useState({});
  const [expanded, setExpanded] = useState({});
  const [since, setSince] = useState({});
  const [connections, setConnections] = useState({});
  const [connOptions, setConnOptions] = useState({});
  const [connEdits, setConnEdits] = useState({});
  const [connBusy, setConnBusy] = useState({});
  // Per-integration: disconnect has been asked for once and is awaiting the
  // second, explicit confirmation. Nothing is sent while this is merely true.
  // Which integration is awaiting disconnect confirmation, or null. One at a
  // time, because the dialog is modal and two pending refusals would be a lie
  // about which one the button ends.
  const [unlinkAsk, setUnlinkAsk] = useState(null);
  // The lens and the query are page-level: they filter BOTH blocks, because a
  // search that emptied one of them would mislead at the moment you looked
  // hardest.
  const [lens, setLens] = useState('all');
  const [query, setQuery] = useState('');
  const pollIntervals = useRef({});
  const aliveRef = useRef(false);

  const group = (identity().groups || []).find((g) => g.id === identity().activeGroupId) || null;

  // ── Fetch statuses for all available integrations ────────────────────────
  const fetchStatuses = useCallback(async () => {
    const providers = INTEGRATIONS.filter((i) => i.statusUrl);
    const results = await Promise.allSettled(
      providers.map(async (integ) => {
        const res = await fetch(urls(integ).status, { credentials: 'include' });
        // In a group context a 404 means no group routes yet — "nothing
        // connected here", not a fault.
        if (!res.ok) {
          return { id: integ.id, status: res.status === 404 ? 'disconnected' : 'error' };
        }
        const data = await res.json();
        return { id: integ.id, ...data };
      }),
    );
    const next = {};
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value) next[r.value.id] = r.value;
    }
    setStatuses((s) => ({ ...s, ...next }));
    return next;
  }, []);

  const stopPolling = (id) => {
    if (pollIntervals.current[id]) {
      clearInterval(pollIntervals.current[id]);
      delete pollIntervals.current[id];
    }
  };

  const startPolling = useCallback((integrationId) => {
    if (pollIntervals.current[integrationId]) return;
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ || !integ.statusUrl) return;
    pollIntervals.current[integrationId] = setInterval(async () => {
      if (!aliveRef.current) { stopPolling(integrationId); return; }
      try {
        const res = await fetch(urls(integ).status, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setStatuses((s) => ({ ...s, [integrationId]: { id: integrationId, ...data } }));
        const hasActive = data.sync && Object.values(data.sync).some((x) => x.active);
        if (!hasActive) stopPolling(integrationId);
      } catch (_) {}
    }, 1500);
  }, []);

  // ── Connection template + field options ──────────────────────────────────
  const loadConnection = useCallback(async (integrationId) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    // A re-read keeps what it is replacing until it has something better —
    // blanking first flashes the card back to "reading…".
    setConnections((c) => ({
      ...c,
      [integrationId]: { ...(c[integrationId]?.data ? { data: c[integrationId].data } : {}), loading: true },
    }));
    let next;
    try {
      const res = await fetch(urls(integ).connection, { credentials: 'include' });
      if (res.status === 404) {
        next = { unsupported: true };
      } else {
        const data = await res.json().catch(() => ({}));
        next = res.ok ? { data } : { error: data.error || `HTTP ${res.status}` };
      }
    } catch (e) {
      next = { error: e.message };
    }
    setConnections((c) => ({ ...c, [integrationId]: next }));
  }, []);

  // Choices come from the connection's own credential — no token to ask with
  // before the OAuth round trip.
  const loadFieldOptions = useCallback(async (integrationId, key) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    setConnOptions((o) => ({ ...o, [integrationId]: { ...(o[integrationId] || {}), [key]: { loading: true } } }));
    let next;
    try {
      const res = await fetch(
        `${urls(integ).connection}/fields/${encodeURIComponent(key)}/options`,
        { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      next = res.ok ? { loading: false, list: data.options || [] } : { loading: false, list: [], failed: true };
    } catch {
      next = { loading: false, list: [], failed: true };
    }
    setConnOptions((o) => ({ ...o, [integrationId]: { ...(o[integrationId] || {}), [key]: next } }));
  }, []);

  // Saved on change rather than behind an Apply button — a scope that looks
  // saved but isn't is the failure that matters here.
  const writeField = useCallback(async (integrationId, key, init, verb) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    setConnBusy((b) => ({ ...b, [integrationId]: { ...(b[integrationId] || {}), [key]: true } }));
    let ok = false;
    try {
      const res = await fetch(
        `${urls(integ).connection}/fields/${encodeURIComponent(key)}`,
        { credentials: 'include', ...init });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) toast(data.error || `Could not ${verb} this`, 'error');
      else { ok = true; toast(`${data.field?.label || key} ${verb === 'clear' ? 'cleared' : 'saved'}`, 'success'); }
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
    setConnBusy((b) => {
      const busy = { ...(b[integrationId] || {}) };
      delete busy[key];
      return { ...b, [integrationId]: busy };
    });
    if (ok) {
      setConnEdits((e) => {
        const edits = { ...(e[integrationId] || {}) };
        delete edits[key];
        return { ...e, [integrationId]: edits };
      });
      // Scope decides whether a connection is usable at all, so the STATUS can
      // change with it.
      await Promise.all([loadConnection(integrationId), fetchStatuses()]);
    }
  }, [loadConnection, fetchStatuses]);

  const setField = (id, key, value) => writeField(id, key, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value }),
  }, 'save');
  const clearField = (id, key) => writeField(id, key, { method: 'DELETE' }, 'clear');

  // ── Plugins ──────────────────────────────────────────────────────────────
  // Through the shared helper so the call carries X-Group-Id: the backend asks
  // Paradigm for the ACTING subject's status (configured, schedules, consent)
  // and builds setup links for that group.
  const loadPlugins = useCallback(async () => {
    try {
      const data = await api.get('/api/plugins');
      setPlugins((data && data.plugins) || []);
      setPluginsError(null);
    } catch (e) {
      setPlugins([]);
      setPluginsError(e.message);
    }
  }, []);

  // ── Init — paint first, then fill in ─────────────────────────────────────
  useEffect(() => {
    if (!active) { aliveRef.current = false; return undefined; }
    aliveRef.current = true;

    // Redirected from the OAuth callback? Clean the URL.
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'integrations') {
      const url = new URL(window.location);
      url.searchParams.delete('view');
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.pathname);
    }

    loadPlugins();
    // Polling starts AFTER statuses land — it reads them to decide which
    // integrations have a sync already in flight.
    fetchStatuses().then((landed) => {
      if (!aliveRef.current) return;
      for (const [id, st] of Object.entries(landed)) {
        if (st.sync && Object.values(st.sync).some((s) => s.active)) startPolling(id);
      }
    });

    return () => {
      aliveRef.current = false;
      Object.keys(pollIntervals.current).forEach(stopPolling);
    };
  }, [active, loadPlugins, fetchStatuses, startPolling]);

  // What each connection is, asked of the connection itself — of EVERY
  // connected integration; the backend 404s for the rest.
  useEffect(() => {
    if (!active) return;
    for (const integ of INTEGRATIONS) {
      if (!integ.statusUrl) continue;
      if ((statuses[integ.id] || {}).status !== 'connected') continue;
      if (connections[integ.id] === undefined) { loadConnection(integ.id); continue; }
      const flds = connections[integ.id]?.data?.fields || [];
      for (const f of flds) {
        if (!f.has_options || f.readonly) continue;
        if ((connOptions[integ.id] || {})[f.key] === undefined) loadFieldOptions(integ.id, f.key);
      }
    }
  }, [active, statuses, connections, connOptions, loadConnection, loadFieldOptions]);

  if (!active) return null;

  // ── Derivations ──────────────────────────────────────────────────────────
  const pluginsFor = (integ) => {
    const names = integ.pluginNames || [];
    return (plugins || []).filter((p) => names.includes(p.name));
  };
  const integrationForPlugin = (plugin) =>
    INTEGRATIONS.find((i) => (i.pluginNames || []).includes(plugin.name)) || null;

  const rowStatus = (integ) =>
    ((statuses[integ.id] || {}).status === 'connected' ? 'connected' : 'disconnected');
  const hasDetail = (integ) => rowStatus(integ) === 'connected';

  // A service the plugin can run is not a second thing to run.
  const pluginCoveredServices = (integ) => {
    const covered = new Set();
    for (const p of pluginsFor(integ)) {
      for (const param of p.input_parameters || []) {
        if (param.key === 'signal') (param.options || []).forEach((o) => covered.add(o));
      }
    }
    return covered;
  };
  const servicesFor = (integ, st) => {
    const reported = Object.keys((st && st.last_synced) || {});
    const all = (identity().activeGroupId && reported.length) ? reported : (integ.services || []);
    const covered = pluginCoveredServices(integ);
    return all.filter((svc) => !covered.has(svc));
  };

  const connected = INTEGRATIONS.filter((i) => rowStatus(i) === 'connected');
  const available = INTEGRATIONS.filter((i) => rowStatus(i) !== 'connected');
  const standalone = (plugins || []).filter((p) => !integrationForPlugin(p));

  // WHOSE PLUGIN IS THIS. GET /plugins is the ecosystem's whole store, not this
  // app's shelf — `filter_by(is_plugin=True, is_active=True)` across every app,
  // narrowed only if the exposure profile names specific plugin_ids, and an
  // empty list there means ALL. So this page has always mixed Integrations' own
  // plugins with every other app's and said nothing about which was which.
  //
  // It matters beyond tidiness: connecting a provider here writes a token into
  // THIS app's vault (USER#<id> / TOKEN#<provider>), and another app's plugin
  // reads ITS owner's vault. "Connect" on this page cannot provision a plugin
  // that belongs to someone else, and until the owner is on screen there is no
  // way to know that from looking.
  const OWN_APP = 'Integrations';
  const isOurs = (p) => !p.parent_app_name || p.parent_app_name === OWN_APP;
  const ourPlugins = standalone.filter(isOurs);
  const otherPlugins = standalone.filter((p) => !isOurs(p));

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleSync = async (integrationId, service) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    try {
      const s = since[integrationId] || undefined;
      const res = await fetch(urls(integ).sync, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(s ? { service, since: s } : { service }),
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || 'Sync failed', 'error'); return; }
      toast(data.since ? `${service} sync started from ${data.since.slice(0, 10)}` : `${service} sync started`, 'success');
      startPolling(integrationId);
    } catch (err) {
      toast(`Sync error: ${err.message}`, 'error');
    }
  };

  // Stopping is a REQUEST, not an act: never claim it stopped before it did.
  const handleCancel = async (integrationId, service) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    setStatuses((s) => {
      const st = s[integrationId];
      if (!st?.sync?.[service]) return s;
      return { ...s, [integrationId]: { ...st, sync: { ...st.sync, [service]: { ...st.sync[service], cancelling: true } } } };
    });
    try {
      const res = await fetch(`${urls(integ).sync}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ service }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast(data.error || 'Could not stop the sync', 'error'); return; }
      toast(data.status === 'cancelling' ? `stopping ${service}…` : 'nothing running', 'success');
      startPolling(integrationId);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleUnlink = async (integrationId) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    try {
      const res = await fetch(urls(integ).unlink, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast(data.error || 'Unlink failed', 'error');
        return;
      }
      setStatuses((s) => { const n = { ...s }; delete n[integrationId]; return n; });
      // The connection that was just ended, and the choices fetched with its
      // credential — left behind they would re-show against the NEXT
      // connection, including someone else's account after a re-link.
      setConnections((c) => { const n = { ...c }; delete n[integrationId]; return n; });
      setConnOptions((o) => { const n = { ...o }; delete n[integrationId]; return n; });
      setConnEdits((e) => { const n = { ...e }; delete n[integrationId]; return n; });
      stopPolling(integrationId);
      toast(`${integ.name} disconnected`, 'success');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleUsernameLink = async (integrationId, username) => {
    const integ = INTEGRATIONS.find((i) => i.id === integrationId);
    if (!integ) return;
    if (!username) { toast('Please enter a username', 'error'); return; }
    try {
      const res = await fetch(integ.linkUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || 'Link failed', 'error'); return; }
      setStatuses((s) => ({ ...s, [integrationId]: { id: integrationId, ...data } }));
      toast(`${integ.name} linked as ${data.username}`, 'success');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const runKey = (pluginId, toolId) => (toolId ? `${pluginId}::${toolId}` : pluginId);

  const runPlugin = async (pluginId, toolId) => {
    const plugin = (plugins || []).find((p) => p.id === pluginId);
    if (!plugin) return;
    const key = runKey(pluginId, toolId);
    // RUNTIME KEYS ONLY, and only what was typed — no defaults, no setup keys.
    // Paradigm merges the subject's saved setup under the run and the run
    // wins, so a default sent from here (signal: 'games') overrode the saved
    // configuration on every manual run.
    const input_parameters = runInputs(plugin, pluginInputs[pluginId]);
    setPluginRuns((r) => ({ ...r, [key]: { running: true } }));
    const gid = identity().activeGroupId || null;
    let next;
    try {
      const res = await fetch(`/api/plugins/${encodeURIComponent(pluginId)}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input_parameters,
          ...(toolId ? { tool_id: toolId } : {}),
          ...(gid ? { group_id: gid } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.consent_required) {
        // 202, not an error: the run is parked until approved — keep the link.
        next = {
          awaitingConsent: true,
          message: data.message || 'This run needs your approval before it can start.',
          reason: data.reason || null,
          consentUrl: data.consent_url || null,
          expiresAt: data.expires_at || null,
        };
      } else if (!res.ok) {
        next = { error: data.error || `HTTP ${res.status}`, consent: data.needs_consent ? data.detail : null };
      } else {
        next = { status: data.status, message: data.message, written: data.written || 0 };
      }
    } catch (e) {
      next = { error: e.message };
    }
    setPluginRuns((r) => ({ ...r, [key]: next }));
  };

  // ── A file for a `file` parameter ───────────────────────────────────────
  // XHR, not fetch, for the progress. The backend hashes the bytes, puts them
  // in Paradigm's storage and returns the rawfile node's id — which is what the
  // run carries. The group rides as a form field, as runPlugin sends it.
  const uploadKey = (pluginId, paramKey) => `${pluginId}::${paramKey}`;
  const setUpload = (k, patch) => setPluginUploads((u) => ({ ...u, [k]: { ...(u[k] || {}), ...patch } }));

  const uploadPluginFile = (pluginId, paramKey, file) => {
    if (!file) return;
    const k = uploadKey(pluginId, paramKey);
    if ((pluginUploads[k] || {}).uploading) return;
    setUpload(k, { uploading: true, progress: 0, error: null, over: false });
    const form = new FormData();
    form.append('file', file);
    const gid = identity().activeGroupId || null;
    if (gid) form.append('group_id', gid);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/plugins/${encodeURIComponent(pluginId)}/upload`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUpload(k, { progress: e.loaded / e.total }); };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || '{}'); } catch (_) {}
      if (xhr.status >= 200 && xhr.status < 300 && body.file_node_id) {
        setPluginInputs((m) => ({
          ...m,
          [pluginId]: {
            ...(m[pluginId] || {}),
            [paramKey]: { id: body.file_node_id, filename: body.filename || file.name, size: body.size_bytes ?? file.size },
          },
        }));
        setUpload(k, { uploading: false, progress: 1, error: null });
      } else {
        const msg = body.error || (xhr.status === 413 ? 'That file is larger than 100 MB.' : `Upload failed (HTTP ${xhr.status}).`);
        setUpload(k, { uploading: false, progress: 0, error: msg });
      }
    };
    xhr.onerror = () => setUpload(k, { uploading: false, progress: 0, error: 'Upload failed: network error.' });
    xhr.send(form);
  };

  const anyUploading = (pluginId) => Object.entries(pluginUploads)
    .some(([k, v]) => k.startsWith(`${pluginId}::`) && v && v.uploading);

  // A compact drop zone: drop a file on it or choose one. Once uploaded it
  // shows the file and offers Replace; a failure stays on it as a line.
  const FileField = ({ p, param, value }) => {
    const k = uploadKey(p.id, param.key);
    const st = pluginUploads[k] || {};
    const accept = typeof param.accept === 'string' ? param.accept
      : Array.isArray(param.accept) ? param.accept.join(',') : undefined;
    const pick = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; uploadPluginFile(p.id, param.key, f); };
    const has = value && typeof value === 'object' && value.id;
    return (
      <div className={`int-file${st.over ? ' is-over' : ''}${st.uploading ? ' is-busy' : ''}`}
           onDragOver={(e) => { e.preventDefault(); if (!st.over) setUpload(k, { over: true }); }}
           onDragLeave={() => setUpload(k, { over: false })}
           onDrop={(e) => {
             e.preventDefault();
             setUpload(k, { over: false });
             const f = e.dataTransfer.files && e.dataTransfer.files[0];
             uploadPluginFile(p.id, param.key, f);
           }}>
        {st.uploading ? (
          <span className="int-file__status">
            uploading… {Math.round((st.progress || 0) * 100)}%
            <span className="int-file__bar"><span style={{ width: `${Math.round((st.progress || 0) * 100)}%` }} /></span>
          </span>
        ) : has ? (
          <span className="int-file__status">
            <span className="int-file__name">{value.filename}</span>
            {value.size != null ? <Meta> · {formatBytes(value.size)}</Meta> : null}
          </span>
        ) : (
          <span className="int-file__status int-conn-hint">drop the file here, or</span>
        )}
        <label className="int-file__pick">
          <input type="file" hidden accept={accept} disabled={!!st.uploading} onChange={pick} />
          <span className="int-file__btn">{has ? 'Replace' : 'Choose file'}</span>
        </label>
        {st.error ? <div className="int-file__err">{st.error}</div> : null}
      </div>
    );
  };

  // ── Fragments ────────────────────────────────────────────────────────────
  const PluginFields = ({ p }) => {
    const inputs = pluginInputs[p.id] || {};
    const runtimeKeys = new Set((p.runtime_params || []).map((x) => x.key));
    const setupHref = withReturnTo(p.setup_url, window.location.href);
    // A WINDOW TURNS A SYNC INTO A ONE-OFF: `since`/`until` mean this run
    // persists no position — right for a catch-up, wrong for a schedule.
    const windowed = ['since', 'until'].filter((k) => runtimeKeys.has(k) && String(inputs[k] ?? '').trim());
    return (
      <>
        {p.input_parameters.length ? (
          <div className="int-params-heading">
            configuration — <a href={setupHref}>change on Paradigm</a>
          </div>
        ) : null}
        {windowed.length ? (
          <div className="int-params-note">
            {windowed.length === 2 ? 'a date range is set' : `${windowed[0]} is set`}
            {' '}— this run will not move the sync position. Right for a one-off catch-up;
            on a schedule it repeats the same window every time. Clear {windowed.length === 2 ? 'both fields' : 'it'} for an ongoing sync.
          </div>
        ) : null}
        {p.input_parameters.map((param) => {
          // SETUP IS PARADIGM'S. Setup parameters (and every secret) are saved
          // on Paradigm's own page and merged under each run there; an app can
          // neither write nor read their values, so none are shown here.
          if (param.secret || !runtimeKeys.has(param.key)) {
            return (
              <div className="int-plugin-field" key={param.key}>
                <span className="int-plugin-field-label">{param.label || param.key}</span>
                <span className="int-conn-hint">set on Paradigm</span>
              </div>
            );
          }
          // Per-run only, and empty unless typed: an empty field sends nothing.
          const val = inputs[param.key] ?? '';
          const set = (v) => setPluginInputs((m) => ({ ...m, [p.id]: { ...(m[p.id] || {}), [param.key]: v } }));
          if (param.type === 'file') {
            return (
              <div className="int-plugin-field" key={param.key}>
                <span className="int-plugin-field-label">{param.label || param.key}</span>
                {FileField({ p, param, value: inputs[param.key] })}
              </div>
            );
          }
          return (
            <label className="int-plugin-field" key={param.key}>
              <span className="int-plugin-field-label">{param.label || param.key}</span>
              {param.type === 'select' ? (
                <select className="int-plugin-input" value={String(val)} onChange={(e) => set(e.target.value)}>
                  <option value="">—</option>
                  {(param.options || []).map((o) => <option value={o} key={o}>{o}</option>)}
                </select>
              ) : (
                <input type="text" className="int-plugin-input" value={String(val)}
                       placeholder={param.type === 'date' ? 'YYYY-MM-DD' : ''}
                       onChange={(e) => set(e.target.value)} />
              )}
            </label>
          );
        })}
      </>
    );
  };

  // A run's outcome, keyed by RUN rather than by plugin — two tools of one
  // plugin produce two results, and a plugin-keyed lookup would show the
  // second one's outcome under both.
  const ToolResult = ({ run }) => {
    if (run.awaitingConsent) {
      return (
        <div className="int-plugin-result int-plugin-result-wait">
          {run.message}
          {run.consentUrl ? (
            <div className="int-plugin-result-sub">
              <a href={run.consentUrl} target="_blank" rel="noopener noreferrer">approve this run</a>
              {run.expiresAt ? ` · expires ${timeUntil(run.expiresAt)}` : ''}
            </div>
          ) : null}
          {run.reason ? <div className="int-plugin-result-sub">reason: {run.reason}</div> : null}
        </div>
      );
    }
    if (run.consent) {
      return (
        <div className="int-plugin-result int-plugin-result-err">
          {run.error || ''}
          <div className="int-plugin-result-sub">{run.consent}</div>
        </div>
      );
    }
    if (run.error) return <div className="int-plugin-result int-plugin-result-err">{run.error}</div>;
    if (run.status) {
      return (
        <div className="int-plugin-result">
          <strong>{run.written} node{run.written === 1 ? '' : 's'} written</strong>
          {run.message ? <div className="int-plugin-result-sub">plugin reported: {run.message}</div> : null}
          {continuesInBackground(run.message)
            ? <div className="int-plugin-result-sub">Importing in batches — the rest continues in the background.</div>
            : null}
        </div>
      );
    }
    return null;
  };

  // THE SCHEDULE IS PARADIGM'S. Read from /status — per tool when Paradigm
  // reports `schedules`, plugin-level when it is older and does not — and
  // changed only on Paradigm's setup page, which every line links to.
  const ScheduleSection = ({ p }) => {
    const setupHref = withReturnTo(p.setup_url, window.location.href);
    const lines = scheduleLines(p);
    return (
      <div className="int-sched">
        <div className="int-card-section-title">schedule</div>
        {lines.map((l) => (
          <div className={`int-sched-line${l.tone === 'on' ? '' : ' int-sched-off'}`} key={l.key}>
            <span className={`int-sched-dot ${l.tone === 'on' ? 'on' : 'off'}`} />
            <span className="int-sched-state">
              {l.label ? <>{l.label}: </> : null}{l.text}
              {l.action ? <> — <a href={setupHref}>{l.action}</a></> : null}
            </span>
          </div>
        ))}
        {setupHref && !lines.some((l) => l.action) ? (
          <div className="int-sched-last">
            <a href={setupHref}>change the schedule on Paradigm →</a>
          </div>
        ) : null}
      </div>
    );
  };

  const ConnectionField = ({ integ, f }) => {
    const busy = (connBusy[integ.id] || {})[f.key];
    const values = Array.isArray(f.value) ? f.value : (f.value ? [f.value] : []);
    const hint = values.length ? f.help : (f.empty_help || f.help);
    const hintEl = hint ? <span className="int-conn-hint">{hint}</span> : null;

    // An invisible ceiling is worse than no ceiling — a readonly field that
    // holds something must be shown; one that holds nothing has nothing to say.
    if (f.readonly) {
      if (!values.length) return null;
      return (
        <div className="int-conn-field">
          <span className="int-conn-field-label">{f.label}</span>
          <div className="int-conn-values">
            {values.map((v, i) => <span className="int-chip" key={i}>{v}</span>)}
            {f.clearable ? (
              <button type="button" className="int-conn-mini" disabled={busy}
                      onClick={() => clearField(integ.id, f.key)}>{busy ? 'clearing…' : 'clear'}</button>
            ) : null}
          </div>
          {hintEl}
        </div>
      );
    }

    const opts = (connOptions[integ.id] || {})[f.key];
    const usable = f.has_options && opts && !opts.loading && (opts.list || []).length;

    let control;
    if (f.has_options && opts && opts.loading) {
      control = <span className="int-conn-hint">loading choices…</span>;
    } else if (usable) {
      // The stored value is offered even when the lookup did not return it —
      // otherwise the next change writes a value nobody chose.
      const list = (opts.list || []).slice();
      const cur = String(f.value || '');
      if (cur && !list.includes(cur)) list.unshift(cur);
      control = (
        <select className="int-conn-select" disabled={busy} value={cur}
                onChange={(e) => setField(integ.id, f.key, e.target.value)}>
          <option value="">— none —</option>
          {list.map((o) => <option value={o} key={o}>{o}</option>)}
        </select>
      );
    } else {
      // Free text is the fallback, not a second-class path.
      const typed = (connEdits[integ.id] || {})[f.key];
      const shown = typed !== undefined ? typed : String(f.value || '');
      control = (
        <>
          <input className="int-conn-input" type="text" placeholder={f.placeholder || ''}
                 value={shown} disabled={busy}
                 onChange={(e) => setConnEdits((m) => ({ ...m, [integ.id]: { ...(m[integ.id] || {}), [f.key]: e.target.value } }))}
                 onKeyDown={(e) => {
                   if (e.key !== 'Enter') return;
                   e.preventDefault();
                   setField(integ.id, f.key, e.target.value.trim());
                 }} />
          <button type="button" className="int-conn-mini" disabled={busy}
                  onClick={() => setField(integ.id, f.key, (shown || '').trim())}>{busy ? 'saving…' : 'set'}</button>
        </>
      );
    }

    return (
      <div className="int-conn-field">
        <span className="int-conn-field-label">{f.label}</span>
        <div className="int-conn-control">{control}</div>
        {hintEl}
      </div>
    );
  };

  // The connection, as the connection describes itself — a declared template,
  // rendered the way a plugin's input_parameters are. It also says what the
  // thing IS: a grant one identifiable person made, that can be ended.
  const ConnectionSection = ({ integ }) => {
    const c = connections[integ.id];
    if (!c || c.unsupported) return null;
    if (c.loading && !c.data) {
      return (
        <div className="int-card-section">
          <div className="int-card-section-title">connection</div>
          <div className="int-conn-hint">reading the connection…</div>
        </div>
      );
    }
    if (c.error || !c.data) {
      // THE DISCONNECT BELONGS HERE MOST OF ALL. This branch rendered a section
      // with no way out, and the caller suppresses its own fallback button
      // whenever this component returns anything — so a connection that could
      // not be READ became one that could not be DROPPED, which is exactly the
      // one you want to drop.
      return (
        <div className="int-card-section">
          <div className="int-card-section-title">connection</div>
          <div className="int-conn-hint">could not read this connection — {c.error || 'no answer'}</div>
          {UnlinkControl({ integ, label: 'disconnect this account' })}
        </div>
      );
    }
    const d = c.data;
    // Who linked it, as a PERSON — `account` is the other side, a different fact.
    const linkedBy = d.linked_by
      ? (d.linked_by.is_you ? 'you' : (d.linked_by.name || 'a member'))
      : null;
    const when = d.linked_at ? ` ${timeAgo(d.linked_at)}` : '';
    const parts = [
      <span className="int-conn-kind" key="k">{d.kind_label || 'connection'}</span>,
      d.account ? <span key="a">{d.account_label || 'account'} <strong>{d.account}</strong></span> : null,
      linkedBy
        ? <span key="l">linked by <strong>{linkedBy}</strong>{when}</span>
        : (when ? <span key="l">linked{when}</span> : null),
    ].filter(Boolean);
    const flds = (d.fields || []);
    return (
      <div className="int-card-section">
        <div className="int-card-section-title">connection</div>
        <div className="int-conn-summary">
          {parts.map((p, i) => <React.Fragment key={i}>{i ? <span className="int-conn-sep">·</span> : null}{p}</React.Fragment>)}
        </div>
        {d.owner === 'group' ? (
          <div className="int-conn-note">
            the group&#39;s syncs run on that member&#39;s account, and stop if they leave the group
          </div>
        ) : null}
        {flds.length ? (
          <div className="int-conn-fields">
            {flds.map((f) => <React.Fragment key={f.key}>{ConnectionField({ integ, f })}</React.Fragment>)}
          </div>
        ) : null}
        {UnlinkControl({ integ, label: 'disconnect this account' })}
      </div>
    );
  };

  // Disconnecting revokes the token at the provider and drops the settings
  // chosen against it. Clicking again does not undo that — the next connection
  // starts empty — so it asks, and the dialog says WHAT GOES rather than "are
  // you sure?". The kit's ConfirmDialog replaces a hand-rolled two-step: one
  // modal, focus-trapped and escapable, instead of a control that quietly
  // became a different control on click.
  const UnlinkControl = ({ integ, label }) => (
    <div className="int-card-actions int-card-actions-conn">
      <Button
        variant="ghost"
        tone="danger"
        size="sm"
        onClick={() => setUnlinkAsk({ id: integ.id, name: integ.name })}
      >{label}</Button>
    </div>
  );

  const ConnectedCard = ({ integ }) => {
    const st = statuses[integ.id] || {};
    const identityLabel = st.email || st.username || '';
    const services = servicesFor(integ, st);
    const blocked = !!st.blocked;
    const anyActive = services.some((svc) => ((st.sync || {})[svc] || {}).active);
    const ps = pluginsFor(integ);
    const conn = connections[integ.id];
    const hasConnSection = conn && !conn.unsupported;

    return (
      <>
        {hasConnSection
          ? ConnectionSection({ integ })
          : (identityLabel ? <div className="int-card-identity">{identityLabel}</div> : null)}
        {st.blocked_reason ? <div className="int-card-blocked">{st.blocked_reason}</div> : null}

        {/* THE PLUGIN MOVED OUT. Its tools are rows under this source now, so
            rendering a run block here too would put TWO run buttons on one plugin
            — which read as two different runs. What stays is the ROUTE sync:
            services this integration can pull without a plugin behind them. */}
        {services.length ? (
          <div className="int-card-section">
            <div className="int-card-section-title">direct sync</div>
            {services.length ? (
              <div className="int-params-heading">
                direct sync{ps.length ? ' — this app fetches and writes these itself' : ''}
              </div>
            ) : null}
            {services.length ? (
              <div className="int-services">
                {services.map((svc) => {
                  const syncInfo = (st.sync || {})[svc] || {};
                  const isActive = syncInfo.active;
                  return (
                    <React.Fragment key={svc}>
                      <div className="int-sync-row">
                        <span className="int-svc-name">{svc}</span>
                        <span className="int-svc-meta">
                          {isActive
                            ? <span className="int-progress-text">{syncInfo.synced || 0}{syncInfo.total ? `/${syncInfo.total}` : ''} synced</span>
                            : syncInfo.stopped_early
                              ? <>stopped — {syncInfo.synced || 0} synced</>
                              : <CursorLabel cursor={(st.last_synced || {})[svc]}
                                             ranAt={(st.last_run_at || {})[svc]}
                                             coverage={(st.last_coverage || {})[svc]} />}
                        </span>
                        {isActive ? (
                          <button type="button" className="int-sync-btn surface-action-btn muted"
                                  disabled={syncInfo.cancelling}
                                  onClick={() => handleCancel(integ.id, svc)}>
                            {syncInfo.cancelling ? 'stopping…' : 'stop'}
                          </button>
                        ) : blocked ? null : (
                          <button type="button" className="int-sync-btn surface-action-btn muted"
                                  onClick={() => handleSync(integ.id, svc)}>sync</button>
                        )}
                      </div>
                      {isActive ? (
                        <div className="int-progress-bar">
                          <div className="int-progress-fill"
                               style={{ width: `${syncInfo.total ? Math.round((syncInfo.synced / syncInfo.total) * 100) : 0}%` }} />
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : null}
            {/* Only offered where a sync walks a history it could reach further
                back into. Empty means "the usual window". */}
            {services.length && !anyActive && !blocked ? (
              <div className="int-since">
                <label className="int-since-label" htmlFor={`since-${integ.id}`}>from</label>
                <input className="int-since-input" type="date" id={`since-${integ.id}`}
                       max={new Date().toISOString().slice(0, 10)}
                       value={since[integ.id] || ''}
                       onChange={(e) => setSince((s) => ({ ...s, [integ.id]: e.target.value || '' }))} />
                <span className="int-since-hint">
                  {since[integ.id] ? 'deep pull — older pushes arrive stamped today' : 'blank = recent only'}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
        {hasConnSection ? null : (
          UnlinkControl({ integ, label: 'disconnect' })
        )}
      </>
    );
  };

  // Not connected: what connecting needs, said before asking.

  // WHAT THIS PUTS IN YOUR GRAPH, ON THE ROW ITSELF.
  //
  // A source's whole purpose is the nodes it produces, and that was only
  // visible after expanding — so a list of sources could not answer the
  // question you open a list of sources to answer. The contract was already
  // being sent (`writes`, from output_declaration) and rendered two clicks
  // deep.
  //
  // THE KIT'S GLYPH VOCABULARY, not a second one. `schemaGlyph` maps a bare
  // schema name to the same mark every other ofself surface draws for it, so
  // an `event` here looks like an `event` in Nucleus. A schema the kit has no
  // glyph for falls back to a plain chip — a degraded mark rather than a wrong
  // one, which is why this can ship before the kit learns `song`.
  const Produces = ({ writes }) => {
    const seen = new Set();
    const schemas = (writes || [])
      .map((w) => w && w.schema_name)
      .filter((n) => n && !seen.has(n) && seen.add(n));
    if (!schemas.length) return null;
    return (
      <span className="int-produces" aria-label={`Produces ${schemas.join(', ')}`}>
        {schemas.slice(0, 6).map((n) => (
          <span className="int-produces__item" key={n} title={n}>
            {hasGlyph(n) ? (
              <span className="int-produces__glyph" aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: schemaGlyph(n, { size: '11px' }) }} />
            ) : null}
            {n}
          </span>
        ))}
        {schemas.length > 6 ? (
          <span className="int-produces__more">+{schemas.length - 6}</span>
        ) : null}
      </span>
    );
  };

  // ── One row per SOURCE, with what it gives you underneath ───────────────
  //
  // THE PAGE USED TO SLICE THIS THE WRONG WAY. "Connections" and "Plugins"
  // were sibling blocks, so every item had to pick a side — and three of seven
  // picked wrong. Google Sync was HIDDEN inside its connection row, so the one
  // plugin with six tools was the one you could not find in the plugin list.
  // Lichess appeared twice, because its registry entry never named its plugin.
  // Chess.com sat in a block implicitly paired with a credential it does not
  // have.
  //
  // They were never two kinds of thing. A source is a source; whether it needs
  // a credential is a STATE, not a category. So: one row each, and the tools
  // it offers nested under it.
  //
  // This also makes the UI match the CONSENT model. You grant per tool — that
  // is what the realm editor and the consent card now say — but you used to
  // run per plugin, so someone could ask for "gmail: ask" and then be shown a
  // single Run button that never mentioned Gmail. The thing you grant, the
  // thing you see and the thing you press are now the same thing.

  // What a source needs before it can do anything, as a sentence rather than a
  // category. `null` means nothing to connect — which is a real answer and the
  // reason chess.com stopped looking broken.
  // THE PLUGIN IS THE AUTHORITY ON ITS OWN CREDENTIAL. This read the hardcoded
  // registry, so a plugin absent from it rendered as "no account needed" with
  // no way to connect — which made REGISTERING A PLUGIN INSUFFICIENT and turned
  // every new provider into a frontend edit.
  //
  // `requires_oauth` and `connected` now come from the plugin list itself, so a
  // brokered plugin nobody has added to INTEGRATIONS still shows the right
  // state and the right button. The registry is decoration now — an icon and a
  // nicer description — not the thing that decides whether you can connect.
  const credentialState = (integ, plugin) => {
    if (plugin && plugin.requires_oauth) {
      return plugin.connected ? 'connected' : 'disconnected';
    }
    // Legacy providers whose credential still lives in this app's own vault.
    if (!integ) return null;
    return rowStatus(integ) === 'connected' ? 'connected' : 'disconnected';
  };

  const SourceRow = ({ integ, plugin, foreign }) => {
    const id = integ ? integ.id : plugin.id;
    const open = !!expanded[id];
    const cred = credentialState(integ, plugin);
    // Connecting a brokered credential happens on Paradigm's setup page for
    // the plugin — the backend's `setup_url`, already on the right host and
    // carrying the acting group. A plugin with no registry entry gets the same.
    const brokered = integ && integ.connectVia === 'paradigm'
      ? ((plugins || []).find((pl) => pl.id === integ.paradigmPluginId) || plugin) : null;
    const setupHref = plugin ? withReturnTo(plugin.setup_url, window.location.href) : null;
    const u = integ
      ? (brokered
          ? { ...urls(integ), connect: withReturnTo(brokered.setup_url, window.location.href) }
          : urls(integ))
      : (plugin && plugin.requires_oauth ? { connect: setupHref } : null);
    const tools = (plugin && Array.isArray(plugin.tools) ? plugin.tools : []) || [];
    const blocked = plugin ? plugin.configured === false : false;
    const name = integ ? integ.name : plugin.name;
    const desc = integ ? integ.desc : plugin.description;

    // A source with nothing to open is a source with no detail to hide. The
    // chevron only appears where pressing it does something.
    const expandable = !!plugin || cred === 'connected' || (integ && integ.usernameLink);

    let action;
    if (cred === 'disconnected') {
      action = u && u.connect
        ? <Button as="a" href={u.connect} variant="solid" size="sm">Connect</Button>
        : <Button variant="outline" size="sm" aria-expanded={open}
                  onClick={() => toggleExpand(id)}>Connect</Button>;
    } else if (expandable) {
      action = (
        <Button variant="ghost" size="sm" aria-expanded={open}
                onClick={() => toggleExpand(id)}>{open ? 'Hide' : 'Open'}</Button>
      );
    }

    // THE BLOCK (owner call 2026-09-25: "each plugin is a BLOCK, arranged in
    // columns and rows — beautiful and brutalist"): the source's mark and its
    // state across the top, its name in the display voice, what it is and
    // what it writes, the one action at its foot. Opened, the block takes the
    // whole row and its detail stands inside it.
    const state = blocked ? 'setup'
      : cred === 'connected' ? 'connected'
      : cred === 'disconnected' ? 'disconnected'
      : 'free';
    const stateWord = blocked ? 'needs setup'
      : cred === 'connected' ? 'connected'
      : cred === 'disconnected' ? 'not connected'
      : 'no account needed';
    return (
      <article className={`int-tile${open ? ' is-open' : ''}`} data-state={state}>
        <button type="button" className="int-tile__face" aria-expanded={expandable ? open : undefined}
                disabled={!expandable}
                onClick={() => expandable && toggleExpand(id)}>
          <span className="int-tile__top">
            <span className="int-tile__icon" aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: integ ? integ.icon : PLUGIN_GLYPH }} />
            <span className="int-tile__state">
              <span className="int-tile__dot" aria-hidden="true" />{stateWord}
            </span>
          </span>
          <span className="int-tile__name">{name}</span>
          {desc ? <span className="int-tile__desc">{desc}</span> : null}
          {plugin ? Produces({ writes: plugin.writes }) : null}
          <span className="int-tile__meta">
            {tools.length ? <span>{tools.length} {tools.length === 1 ? 'tool' : 'tools'}</span> : null}
            {plugin && plugin.is_verified ? <span title="verified by Paradigm">verified</span> : null}
            {foreign ? <span>by {plugin.parent_app_name}</span> : null}
          </span>
        </button>
        {action ? <div className="int-tile__action">{action}</div> : null}

        {open ? (
          <div className="int-tile__detail">
            {/* The credential's own controls, where the credential is. */}
            {cred === 'connected' ? ConnectedCard({ integ }) : null}
            {cred === 'disconnected' && integ && integ.usernameLink
              ? <UsernameConnect integ={integ} onLink={handleUsernameLink} /> : null}

            {plugin ? (
              <>
                {blocked ? (
                  <Banner tone="warning" className="plug__alert">
                    {(plugin.missing_fields || []).length
                      ? `${plugin.missing_fields.join(', ')} ${plugin.missing_fields.length === 1 ? 'has' : 'have'} no value yet.`
                      : 'Some required configuration is missing.'}
                    {' '}
                    <a href={setupHref}>
                      Set it up on Paradigm →
                    </a>
                  </Banner>
                ) : null}

                {tools.length ? (
                  <ul className="int-tools">
                    {tools.map((t) => (
                      <React.Fragment key={t.tool_id}>
                        {ToolRow({ plugin, tool: t, blocked, connected: cred !== 'disconnected' })}
                      </React.Fragment>
                    ))}
                  </ul>
                ) : (
                  // A plugin that declares no tools still runs — as one thing,
                  // unnamed. Honest, and the nudge to migrate it.
                  <div className="int-tools int-tools--untooled">
                    {ToolRow({ plugin, tool: null, blocked, connected: cred !== 'disconnected' })}
                  </div>
                )}

                {plugin.input_parameters.length
                  ? <div className="int-plugin-fields">{PluginFields({ p: plugin })}</div> : null}
                {ScheduleSection({ p: plugin })}
                <div className="plug__actions">
                  <Button as="a" variant="ghost" size="sm" href={setupHref}>
                    {blocked ? 'Set up' : 'Configure'}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </article>
    );
  };

  // ONE TOOL, ONE ROW, ONE BUTTON. The unit you grant is now the unit you run.
  const ToolRow = ({ plugin, tool, blocked, connected }) => {
    const key = runKey(plugin.id, tool ? tool.tool_id : null);
    const run = pluginRuns[key] || {};
    // WHY IT CANNOT RUN, ON THE ROW. A disabled button with no reason is just
    // a broken one, and the three reasons are genuinely different fixes.
    const uploading = anyUploading(plugin.id);
    const why = !connected ? 'connect the account first'
      : blocked ? 'finish setting it up'
      : uploading ? 'the file is still uploading'
      : missingRunMessage(plugin, pluginInputs[plugin.id]);

    return (
      <li className="int-tool">
        <div className="int-tool__label">
          <span className="int-tool__name">{tool ? (tool.name || tool.tool_id) : 'Run'}</span>
          {tool && tool.description
            ? <Meta className="int-tool__desc">{tool.description}</Meta> : null}
          {/* PER TOOL, because the tool is the unit you press. A plugin's
              contract says what the plugin can write; what THIS one writes is
              the question you have with your finger over the button. Falls
              back to the plugin's contract when a tool declares none of its
              own. */}
          {Produces({ writes: (tool && tool.writes) || plugin.writes })}
          {why ? <Meta className="int-tool__why">{why}</Meta> : null}
        </div>
        <div className="int-tool__action">
          <Button size="sm" variant={why ? 'ghost' : 'outline'}
                  busy={!!run.running} disabled={!!run.running || !!why}
                  onClick={() => runPlugin(plugin.id, tool ? tool.tool_id : undefined)}>
            {run.running ? 'Running' : 'Run'}
          </Button>
        </div>
        {run.message || run.error || run.awaitingConsent ? (
          <div className="int-tool__result">
            {ToolResult({ run })}
          </div>
        ) : null}
      </li>
    );
  };

  // ── One list of SOURCES ─────────────────────────────────────────────────
  // Each integration carries its plugin; each plugin with no integration is a
  // source in its own right. Nothing is hidden inside anything else, and
  // nothing appears twice — the two faults the old split produced.
  // A plain computation, not useMemo: this runs below `if (!active) return
  // null`, and a hook there changes the hook count whenever `active` flips
  // ("Rendered more hooks than during the previous render"). It is a loop
  // over a handful of rows, so there is nothing to memoise.
  const sources = (() => {
    const out = [];
    const claimed = new Set();
    for (const integ of INTEGRATIONS) {
      // `pluginNames` is the registry's link. Lichess never declared one, which
      // is why it used to render as a connection AND again as a plugin; match
      // on the name as a fallback so a missing link cannot duplicate a row.
      const mine = pluginsFor(integ);
      const byName = (plugins || []).find(
        (pl) => pl.name.toLowerCase().startsWith(integ.name.toLowerCase()));
      const plugin = mine[0] || byName || null;
      if (plugin) claimed.add(plugin.id);
      out.push({ key: integ.id, integ, plugin, foreign: false });
    }
    for (const pl of plugins || []) {
      if (claimed.has(pl.id)) continue;
      out.push({ key: pl.id, integ: null, plugin: pl, foreign: !isOurs(pl) });
    }
    return out;
  })();

  const q = query.trim().toLowerCase();
  const shown = sources.filter((src) => {
    const name = src.integ ? src.integ.name : src.plugin.name;
    const desc = src.integ ? src.integ.desc : src.plugin.description;
    const toolNames = (src.plugin?.tools || [])
      .map((t) => `${t.tool_id} ${t.name || ''}`).join(' ');
    // Tool names are searchable: looking for "calendar" should find Google,
    // which is the whole point of naming the tools.
    if (q && !`${name} ${desc || ''} ${toolNames}`.toLowerCase().includes(q)) return false;
    const needsWork = (src.integ && rowStatus(src.integ) !== 'connected')
      || (src.plugin && src.plugin.configured === false);
    if (lens === 'ready') return !needsWork;
    if (lens === 'setup') return needsWork;
    return true;
  });

  const needsAttention = sources.filter((src) =>
    (src.integ && rowStatus(src.integ) !== 'connected')
    || (src.plugin && src.plugin.configured === false)).length;

  return (
    <div className="int-wrap">
      {group ? (
        <Banner tone="info" className="int-actingbar">
          Acting as <strong>{group.name}</strong> — what these bring in belongs to the group.
        </Banner>
      ) : null}

      <div className="int-controls">
        <SegmentedControl
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'ready', label: 'Ready' },
            { value: 'setup', label: needsAttention ? `Needs setup · ${needsAttention}` : 'Needs setup' },
          ]}
        />
        <SearchBar
          value={query}
          onChange={(e) => setQuery(e?.target ? e.target.value : e)}
          placeholder="Search sources and tools"
          className="int-search"
        />
      </div>

      <Block className="int-block">
        {plugins === null && !pluginsError ? (
          <div className="int-loading"><Spinner /> <Meta>Loading…</Meta></div>
        ) : shown.length ? (
          <div className="int-tiles">
            {shown.map((src) => (
              <React.Fragment key={src.key}>
                {SourceRow({ integ: src.integ, plugin: src.plugin, foreign: src.foreign })}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <EmptyState className="int-empty">
            {q ? `Nothing matches “${query}”.`
               : lens === 'setup' ? 'Everything is set up.'
               : 'Nothing here yet.'}
          </EmptyState>
        )}
        {pluginsError ? (
          <Banner tone="danger" className="int-rows-note">
            Could not load plugins — {pluginsError}
          </Banner>
        ) : null}
      </Block>

      <ConfirmDialog
        open={!!unlinkAsk}
        title={unlinkAsk ? `Disconnect ${unlinkAsk.name}?` : ''}
        confirmLabel="Disconnect"
        tone="danger"
        onConfirm={() => { const t = unlinkAsk; setUnlinkAsk(null); if (t) handleUnlink(t.id); }}
        onCancel={() => setUnlinkAsk(null)}
      >
        {unlinkAsk ? (
          <>This revokes access at {unlinkAsk.name} and deletes the settings you
          chose for it here. Anything already imported into your graph stays.</>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
